<svelte:options />

<script>
    import { slugify } from '@repo/config/Utils';


    import { createEventDispatcher } from 'svelte';
    /**
     * @typedef {Object} Props
     * @property {string} [label]
     * @property {any} name
     * @property {string} [instruction]
     * @property {string} [value]
     * @property {any} [associatedID]
     * @property {boolean} [labelSrOnly]
     * @property {any} [disabled]
     * @property {boolean} [checked]
     */
    let {
        label = '',
        name,
        instruction = '',
        value = $bindable('unchecked'),
        associatedID = null,
        labelSrOnly = false,
        disabled = null,
        checked = $bindable(false),
        onchange = () => {},
    } = $props();

    const dispatch = createEventDispatcher();

    function handleClick(e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();

        if (disabled) return;

        // Where value is string the .value can be read off the element as a string value. If value is bool then .value doesn't change and .checked should be pulled off element
        if (value === 'checked' || value === 'unchecked') {
            value = value === 'checked' ? 'unchecked' : 'checked';
            if (value === 'checked') {
                checked = true;
            } else {
                checked = false;
            }
        } else {
            checked = !checked;
        }

        onchange({
            detail: {
                originalEvent: e,
                name,
                value,
                checked,
                associatedID,
            }
        });
    }

    // Keep the rendered input in sync when the value prop changes after mount
    // (e.g. the parent toggles state without going through handleClick)
    $effect(() => {
        if (value === 'checked' || value === true || value === 'true') {
            checked = true;
        } else if (value === 'unchecked' || value === false || value === 'false') {
            checked = false;
        }
    });

    export {
        label,
        name,
        instruction,
        value,
        associatedID,
        labelSrOnly,
        disabled,
        checked,
    };
</script>

<div class="checkbox" class:has-content={label} class:disabled>
    <input
        id={slugify(`${name}-checkbox`)}
        {name}
        {value}
        type="checkbox"
        onchange={handleClick}
        class="interactive sr-only input"
        aria-label={instruction}
        {checked}
        {disabled}
    />
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <label
        onclick={handleClick}
        onkeypress={handleClick}
        for={slugify(`${name}-checkbox`)}
        class="interactive {label && labelSrOnly ? 'sr-offset' : ''}"
        tabindex="-1"
    >
        {#if labelSrOnly}
            <span class="sr-only">{label}</span>
        {:else}
            {label}
        {/if}
        {#if instruction}
            <small>{instruction}</small>
        {/if}
    </label>
</div>

<style>
    .checkbox {
        padding: 0.5rem;
        border-radius: 10rem;
        width: 24px;
        height: 24px;
    }

    .checkbox.has-content {
        width: auto;
        height: auto;
    }

    .checkbox.disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .checkbox.disabled * {
        cursor: not-allowed;
    }

    label small {
        font-size: 0.75rem;
        font-weight: 400;
        text-transform: none;
        margin-left: 0.25rem;
        opacity: 0.5;
    }

    label {
        position: relative;
        display: block;
        padding-left: 23px;
        text-transform: uppercase;
        font-weight: 600;
        font-size: 0.75rem;
        margin-left: 0.25rem;
        color: rgba(255, 255, 255, 0.6);
    }

    .checkbox.has-content label {
        display: inline-block;
        line-height: 18px;
        padding-left: 25px;
    }

    /* Spacing between options */
    label:not(:last-of-type) {
        margin-bottom: 10px;
    }

    /**
    Empty checkbox
    */
    label:before {
        position: absolute;
        left: 0;
        top: 0px;

        content: '';
        width: 13px;
        height: 13px;

        border: 2px solid #cbcbcb;
        border-radius: 2px;
    }

    label.sr-offset:before {
        top: -13px;
    }

    /**
    Checked state - adds a pure-CSS checkmark inside of box
    */
    input:checked + label::before {
        background: rgb(74, 198, 255) !important;
        border-color: rgb(74, 198, 255) !important;
    }

    input:checked + label:after {
        position: absolute;
        left: 3px;
        top: 4px;

        content: '';
        width: 8px;
        height: 4px;
        margin-right: 8px;

        border-left: 2px solid black;
        border-bottom: 2px solid black;

        transform: rotate(-45deg);
    }

    input:checked + label.sr-offset:after {
        top: -8px;
    }

    /**
    Focus state
    */
    input:focus + label:before {
        outline: 5px auto Highlight;
        outline: 5px auto -webkit-focus-ring-color;
        outline-offset: 4px;
    }

    .checkbox:not(.has-content) label {
        height: 100%;
    }

    .checkbox:not(.has-content) label::before {
        top: 50%;
        transform: translateY(-50%);
    }

    .checkbox:not(.has-content) input:checked + label:after {
        top: calc(50% - 2px);
        transform: translateY(-50%) rotate(-45deg);
    }
</style>
